/** @jsx jsx */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Focusable } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { MINIMISED_DROPZONE_CONTAINER_ID } from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller/src/ui/bottom-right-corner/outlet/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireScreenAnalyticsDeferred,
	MountEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useEnterFocusState } from '../../../controllers/enter-focus-state/index.tsx';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal/index.tsx';
import { TransitionState } from '../../../controllers/use-minimizable-modal/types.tsx';
import type { MinimizedContainerProps } from '../../../types.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	minimizedWrapper: {
		border: 'none',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.raised'),
		height: '48px',
		width: '300px',
		borderRadius: token('border.radius.200'),
		display: 'flex',
		alignItems: 'center',
		paddingTop: token('space.0'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.200'),
		cursor: 'pointer',
		transition: 'background 0.1s ease-out',
		boxShadow: token('elevation.shadow.overlay'),

		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			outline: 'none',
		},

		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
			outline: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:focus:not(:focus-visible)': {
			boxShadow: 'none',
			outline: 'none',
		},
	},
	minimizedText: {
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		color: token('color.text'),
		font: token('font.heading.small'),
	},
	minimizedOuterWrapper: {
		'&:focus': {
			outline: 'none',
		},
	},
});

export const Minimized = ({ children, className }: MinimizedContainerProps) => {
	const { unminimizeModal, previousView, isTransitionInState, setShouldFocusChanger } =
		useMinimizableModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { minimizedButtonRef, onKeyDown, wrapperRef } = useEnterFocusState();

	const isViewEntered = isTransitionInState(['minimized'], TransitionState.ENTERED);

	const unminimize = () => {
		if (!isViewEntered) return;
		setShouldFocusChanger(true);
		unminimizeModal('modal');
		const analyticsEvent = createAnalyticsEvent({
			action: 'container clicked',
			actionSubject: 'minimized',
		});
		fireUIAnalytics(analyticsEvent, {
			source: 'globalIssueCreateModal',
			previousGICViewState: previousView,
		});
	};

	return (
		<JiraBottomRightCornerOutlet orderFromRight={3} id="issue-create-minimised-modal">
			<div
				css={styles.minimizedOuterWrapper}
				id={MINIMISED_DROPZONE_CONTAINER_ID}
				tabIndex={-1}
				ref={wrapperRef}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={className}
				data-testid={
					// testid should only be available when view is not transitioning
					// otherwise CY tries to click the view before its last position
					isViewEntered ? 'minimizable-modal.ui.modal-container.minimized-outer-wrapper' : undefined
				}
			>
				<MountEvent onMount={fireScreenAnalyticsDeferred} />

				<Focusable
					isInset
					ref={minimizedButtonRef}
					testId={isViewEntered ? 'minimizable-modal.ui.modal-container.minimized' : undefined}
					onKeyDown={onKeyDown}
					onClick={unminimize}
					aria-label={formatMessage(messages.ariaLabel)}
					xcss={styles.minimizedWrapper}
				>
					<div css={styles.minimizedText}>{children}</div>
				</Focusable>
			</div>
		</JiraBottomRightCornerOutlet>
	);
};
